import {
  IAccount,
  IAccountService,
  IProperty,
  IPropertySelection,
  IUserContext,
} from "@origin-digital/event-dispatcher";
import { readPropertyFromStorage } from "../UserContext/UserContextService";
import { isPresent } from "./utils";
import { mapToAccountServices } from "./accountServices";

interface PropertyAccounts {
  property: IProperty;
  accounts: IAccount[];
}

const getPropertyAccountGroups = (accounts: IAccount[]): PropertyAccounts[] => {
  const propertyIdToGroup: Record<string, PropertyAccounts> = {};
  accounts.forEach((account) => {
    account.services.forEach((service) => {
      if (!propertyIdToGroup[service.property.id]) {
        propertyIdToGroup[service.property.id] = {
          property: service.property,
          accounts: [],
        };
      }
      if (!propertyIdToGroup[service.property.id].accounts.includes(account)) {
        propertyIdToGroup[service.property.id].accounts.push(account);
      }
    });
  });
  return Object.values(propertyIdToGroup);
};

/**
 * Create a list of possible property selections for the given user data.
 * Each property is a grouping containing a single property and a collection of account-service combinations for that property.
 */
export const getAllPropertiesForUser = (
  userContext?: IUserContext
): IPropertySelection[] => {
  if (!userContext) {
    return [];
  }

  const propertyAccountGroups = getPropertyAccountGroups(userContext.accounts);

  return propertyAccountGroups
    .map(({ property, accounts }) => {
      const accountServices: IAccountService[] = mapToAccountServices(accounts);
      if (accountServices.length === 0) {
        return undefined;
      }
      return {
        ...property,
        accountServices,
        selectedAccountService: accountServices[0],
        isPrevious: accountServices.every(
          (accountService) => accountService.isClosed
        ),
      };
    })
    .filter(isPresent);
};

/**
 * Get property from storage and return its identifiers.
 * Only identifiers are returned to prevent anyone from the object that's returned, and instead look up the matching item in the in-memory list.
 */
export const readPropertyIdsFromStorage = (): {
  propertyId: string;
  selectedServiceId?: string;
} | null => {
  const loadedProperty = readPropertyFromStorage();
  return loadedProperty
    ? {
        propertyId: loadedProperty.id,
        selectedServiceId: loadedProperty.selectedAccountService?.serviceId,
      }
    : null;
};

/**
 * If possible, set the property.selectedAccountService to the given serviceId.
 */
export const selectServiceOnProperty = (
  propertySelection?: IPropertySelection | null,
  serviceId?: string
) => {
  if (!propertySelection) {
    return;
  }
  const findServiceById = propertySelection.accountServices.find(
    (accountService) => accountService.serviceId === serviceId
  );
  if (findServiceById) {
    propertySelection.selectedAccountService = findServiceById;
  }
};

/**
 * Copy the propertyList, and replace the given item with matching id.
 */
export const copyAndReplaceProperty = (
  propertyList: IPropertySelection[] | undefined,
  newProperty: IPropertySelection
): IPropertySelection[] | undefined => {
  if (!propertyList) {
    return propertyList;
  }
  return propertyList.map((property) =>
    property.id === newProperty.id ? newProperty : property
  );
};
